<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import TextField from '@/components/input/TextField.vue'
import ColorPicker from '@/components/input/ColorPicker.vue'
import { normalName, required } from '@/validation'
import ItemEditIcon from '@/components/button/ButtonEdit.vue'
import ItemDeleteIcon from '@/components/button/ButtonDelete.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'

const { t } = useI18n()

const fetchQuery = useQuery(gql`
  query IssueLabelViewData {
    issue {
      orgIssueLabels {
        id
        name
        color
      }
    }
  }
`)
type LocalIssueLabel = {
  id: string
  name: string
  color: string
}
const items = computed<LocalIssueLabel[]>(() => fetchQuery.result.value?.issue.orgIssueLabels || [])

type CreateModel = Partial<{ name: string; color: string }>
const createModel = reactive<CreateModel>({})
const createValidation = useVuelidate<CreateModel>(
  {
    name: { required, normalName },
    color: {
      required,
      // simple hex color validation aligned with backend
      hex: (v: string) => /^#[0-9a-fA-F]{6}$/.test(v || ''),
    },
  },
  createModel,
)
const createMutation = useMutation(gql`
  mutation IssueLabelViewCreate($command: CreateIssueLabel!) {
    issue {
      createIssueLabel(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  createModel.name = undefined
  createModel.color = '#000000'
  await createDialog.value?.open(() =>
    createMutation
      .mutate({ command: createModel as { name: string; color: string } })
      .then(() => fetchQuery.refetch()),
  )
}

type UpdateModel = Partial<{ id: string; name?: string; color?: string }>
const updateModel = reactive<UpdateModel>({})
const updateValidation = useVuelidate<UpdateModel>(
  {
    name: { required, normalName },
    color: {
      required,
      hex: (v: string) => /^#[0-9a-fA-F]{6}$/.test(v || ''),
    },
  },
  updateModel,
)
const updateMutation = useMutation(gql`
  mutation IssueLabelViewUpdate($command: UpdateIssueLabel!) {
    issue {
      updateIssueLabel(command: $command) {
        id
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (item: LocalIssueLabel) => {
  updateModel.id = item.id
  updateModel.name = item.name
  updateModel.color = item.color
  await updateDialog.value?.open(() =>
    updateMutation
      .mutate({ command: updateModel as { id: string; name: string; color: string } })
      .then(() => fetchQuery.refetch()),
  )
}

const deleteMutation = useMutation(gql`
  mutation IssueLabelViewDelete($id: ID!) {
    issue {
      deleteIssueLabel(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof AsyncDeleteDialog>>()
const doDelete = async (item: LocalIssueLabel) => {
  await deleteDialog.value?.open(item.name, () =>
    deleteMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
  )
}

const search = ref('')
const headers = [
  { key: 'name', title: t('entity.issueLabel.field.name') },
  { key: 'color', title: t('entity.issueLabel.field.color'), width: 140 },
  { key: 'actions', width: 100, sortable: false, align: 'end' },
]
const sort = [{ key: 'name', order: 'asc' }]
</script>

<template>
  <h1>{{ t('entity.issueLabel.plural') }}</h1>

  <data-table
    :items="items"
    :headers="headers"
    :loading="fetchQuery.loading.value"
    :sort-by="sort"
    :search="search"
    density="comfortable"
  >
    <template #header.actions>
      <data-table-search v-model="search" />
      <data-table-create-button @click="doCreate" />
    </template>
    <template #item.actions="{ item }: { item: LocalIssueLabel }">
      <item-edit-icon @click="doUpdate(item)" />
      <item-delete-icon @click="doDelete(item)" />
    </template>
    <template #item.color="{ item }: { item: LocalIssueLabel }">
      <div class="d-flex align-center">
        <div
          :style="{
            background: item.color,
            width: '18px',
            height: '18px',
            borderRadius: '3px',
            marginRight: '8px',
            border: '1px solid #ccc',
          }"
        />
        <span>{{ item.color }}</span>
      </div>
    </template>
  </data-table>

  <async-delete-dialog ref="deleteDialog" :warning="t('entity.issueLabel.deleteWarning')" />

  <upsert-dialog ref="createDialog" type="create" :validation="createValidation">
    <text-field
      v-model="createModel.name"
      :label="t('entity.issueLabel.field.name')"
      required
      :validation="createValidation.name"
    />
    <color-picker v-model="createModel.color" :label="t('entity.issueLabel.field.color')" />
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.issueLabel.singular')"
    :validation="updateValidation"
  >
    <text-field
      v-model="updateModel.name"
      required
      :label="t('entity.issueLabel.field.name')"
      :validation="updateValidation.name"
    />
    <color-picker v-model="updateModel.color" :label="t('entity.issueLabel.field.color')" />
  </upsert-dialog>
</template>

<style scoped lang="scss"></style>
